import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';

interface ConfirmationOptions {
  title?: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
  requireInput?: boolean;
  inputLabel?: string;
  expectedInput?: string;
}

interface ConfirmationState {
  isOpen: boolean;
  options: ConfirmationOptions;
  onConfirm: (inputValue?: string) => void;
  onCancel: () => void;
  inputValue: string;
}

export const useConfirmation = () => {
  const [state, setState] = useState<ConfirmationState>({
    isOpen: false,
    options: { description: '' },
    onConfirm: () => {},
    onCancel: () => {},
    inputValue: '',
  });

  const confirm = (options: ConfirmationOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        options: {
          title: 'Confirmar ação',
          confirmText: 'Confirmar',
          cancelText: 'Cancelar',
          variant: 'default',
          ...options,
        },
        inputValue: '',
        onConfirm: (inputValue) => {
          // Se requer input, verificar se o valor está correto
          if (options.requireInput && options.expectedInput) {
            if (inputValue !== options.expectedInput) {
              resolve(false);
              return;
            }
          }
          setState(prev => ({ ...prev, isOpen: false, inputValue: '' }));
          resolve(true);
        },
        onCancel: () => {
          setState(prev => ({ ...prev, isOpen: false, inputValue: '' }));
          resolve(false);
        },
      });
    });
  };

  const ConfirmationDialog = () => {
    const [inputValue, setInputValue] = useState('');

    const handleConfirm = () => {
      if (state.options.requireInput) {
        state.onConfirm(inputValue);
      } else {
        state.onConfirm();
      }
    };

    const handleCancel = () => {
      setInputValue('');
      state.onCancel();
    };

    return (
      <AlertDialog open={state.isOpen} onOpenChange={(open) => {
        if (!open) {
          handleCancel();
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{state.options.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {state.options.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {state.options.requireInput && (
            <div className="py-4">
              <label className="text-sm font-medium text-gray-700">
                {state.options.inputLabel || 'Digite para confirmar:'}
              </label>
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={state.options.expectedInput}
                className="mt-2"
                autoFocus
              />
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>
              {state.options.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirm}
              disabled={state.options.requireInput && inputValue !== state.options.expectedInput}
              className={state.options.variant === 'destructive' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {state.options.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  return {
    confirm,
    ConfirmationDialog,
  };
};
